import { ContactPicker } from '@agentic-flow-studio/shared/components/contacts/ContactPicker';
import {
  AddressAutocomplete,
  type AddressResult,
} from '@agentic-flow-studio/shared/components/job-plan/AddressAutocomplete';
import { JobAttachmentsSection } from '@agentic-flow-studio/shared/components/job-plan/JobAttachmentsSection';
import { JobLocationMap } from '@agentic-flow-studio/shared/components/job-plan/JobLocationMap';
import { JobTimingConstraintsInput } from '@agentic-flow-studio/shared/components/job-plan/JobTimingConstraintsInput';
import { formatOffsetTime, parseOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type {
  AvailabilityWindow,
  JobStatus,
  JobTimingMode,
} from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  Camera,
  Check,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Plus,
  Trash2,
  User,
  Wrench,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface JobFormPageProps {
  mode: 'create' | 'edit';
}

export default function JobFormPage({ mode }: JobFormPageProps) {
  const { orgId, workspaceId, jobId } = useParams<{
    orgId: string;
    workspaceId: string;
    jobId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const initialContactId = searchParams.get('contactId')
    ? parseInt(searchParams.get('contactId')!, 10)
    : null;

  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const basePath = `/org/${orgId}/workspace/${workspaceId}/jobs`;
  const today = new Date().toISOString().split('T')[0];

  // Form states
  const [title, setTitle] = useState('');
  const [formattedAddress, setFormattedAddress] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<number | null>(initialContactId);

  const [serviceDate, setServiceDate] = useState(today);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [windowStart, setWindowStart] = useState('08:00');
  const [windowEnd, setWindowEnd] = useState('17:00');
  const [timingMode, setTimingMode] = useState<JobTimingMode>('fixed');
  const [earliestServiceDate, setEarliestServiceDate] = useState<string | null>(null);
  const [deadlineServiceDate, setDeadlineServiceDate] = useState<string | null>(null);
  const [deadlineOffsetSeconds, setDeadlineOffsetSeconds] = useState<number | null>(null);
  const [availabilityWindows, setAvailabilityWindows] = useState<AvailabilityWindow[] | null>(null);
  const [priority, setPriority] = useState<number>(0);
  const [status, setStatus] = useState<JobStatus>('unassigned');
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);

  // Inline new skill input
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillDescription, setNewSkillDescription] = useState('');

  // Data queries
  const { data: skills = [], isLoading: isSkillsLoading } = trpc.jobPlan.skills.list.useQuery();

  const numJobId = jobId ? parseInt(jobId, 10) : 0;
  const { data: existingJob, isLoading: isJobLoading } = trpc.jobPlan.jobs.getById.useQuery(
    { id: numJobId },
    { enabled: mode === 'edit' && numJobId > 0 }
  );

  const { data: selectedContact } = trpc.contacts.getById.useQuery(
    { id: contactId! },
    { enabled: !!contactId }
  );

  // Auto-fill from contact on create mode if fields are empty
  useEffect(() => {
    if (mode === 'create' && selectedContact) {
      const contactAddr = [
        selectedContact.addressStreet,
        selectedContact.addressCity,
        selectedContact.addressState,
        selectedContact.addressPostalCode,
      ]
        .filter(Boolean)
        .join(', ');

      if (contactAddr && !formattedAddress) {
        setFormattedAddress(contactAddr);
      }
      if (!title) {
        setTitle(`Service Visit for ${selectedContact.displayName || selectedContact.label}`);
      }
    }
  }, [mode, selectedContact, formattedAddress, title]);

  // Populate form on edit
  useEffect(() => {
    if (mode === 'edit' && existingJob) {
      setTitle(existingJob.title);
      setFormattedAddress(existingJob.formattedAddress || '');
      setLatitude(existingJob.latitude ?? null);
      setLongitude(existingJob.longitude ?? null);
      setGooglePlaceId(existingJob.googlePlaceId ?? null);
      setContactId(existingJob.contactId ?? null);
      setServiceDate(existingJob.serviceDate);
      setDurationMinutes(Math.round(existingJob.estimatedDurationSeconds / 60));
      setWindowStart(
        existingJob.windowStartOffsetSeconds != null
          ? formatOffsetTime(existingJob.windowStartOffsetSeconds)
          : '08:00'
      );
      setWindowEnd(
        existingJob.windowEndOffsetSeconds != null
          ? formatOffsetTime(existingJob.windowEndOffsetSeconds)
          : '17:00'
      );
      setTimingMode(existingJob.timingMode || 'fixed');
      setEarliestServiceDate(existingJob.earliestServiceDate ?? null);
      setDeadlineServiceDate(existingJob.deadlineServiceDate ?? null);
      setDeadlineOffsetSeconds(existingJob.deadlineOffsetSeconds ?? null);
      setAvailabilityWindows(existingJob.availabilityWindows ?? null);
      setPriority(existingJob.priority ?? 0);
      setStatus(existingJob.status);
      setSelectedSkillIds((existingJob.requiredSkills ?? []).map((s) => s.id));
    }
  }, [mode, existingJob]);

  const createSkillMutation = trpc.jobPlan.skills.create.useMutation({
    onSuccess: (newSkill) => {
      toast.success(`Skill "${newSkill.name}" created`);
      setNewSkillName('');
      setNewSkillDescription('');
      utils.jobPlan.skills.list.invalidate();
      setSelectedSkillIds((prev) => [...prev, newSkill.id]);
    },
    onError: (err) => toast.error(err.message),
  });

  const createJobMutation = trpc.jobPlan.jobs.create.useMutation({
    onSuccess: (newJob) => {
      toast.success('Job created successfully');
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
      if (newJob) {
        navigate(`${basePath}/${newJob.id}`);
      } else {
        navigate(basePath);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const updateJobMutation = trpc.jobPlan.jobs.update.useMutation({
    onSuccess: () => {
      toast.success('Job updated successfully');
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
      navigate(`${basePath}/${numJobId}`);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteJobMutation = trpc.jobPlan.jobs.delete.useMutation({
    onSuccess: () => {
      toast.success('Job deleted');
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleQuickAddSkill = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;
    createSkillMutation.mutate({
      name: newSkillName.trim(),
      description: newSkillDescription.trim() || undefined,
    });
  };

  const handleSelectAddress = (result: AddressResult) => {
    setFormattedAddress(result.formattedAddress);
    setLatitude(result.latitude);
    setLongitude(result.longitude);
    setGooglePlaceId(result.googlePlaceId);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !formattedAddress.trim()) {
      toast.error('Please fill in title and address');
      return;
    }

    if (mode === 'create') {
      createJobMutation.mutate({
        title: title.trim(),
        formattedAddress: formattedAddress.trim(),
        latitude,
        longitude,
        googlePlaceId,
        serviceDate,
        estimatedDurationSeconds: durationMinutes * 60,
        windowStartOffsetSeconds: parseOffsetTime(windowStart),
        windowEndOffsetSeconds: parseOffsetTime(windowEnd),
        timingMode,
        earliestServiceDate:
          timingMode === 'range' ? earliestServiceDate || serviceDate : undefined,
        deadlineServiceDate:
          timingMode === 'range' ? deadlineServiceDate || serviceDate : undefined,
        deadlineOffsetSeconds: deadlineOffsetSeconds ?? undefined,
        availabilityWindows:
          timingMode === 'multi_slot' ? (availabilityWindows ?? undefined) : undefined,
        priority,
        contactId: contactId ?? undefined,
        requiredSkillIds: selectedSkillIds,
      });
    } else {
      if (!numJobId) return;
      updateJobMutation.mutate({
        id: numJobId,
        title: title.trim(),
        formattedAddress: formattedAddress.trim(),
        latitude,
        longitude,
        googlePlaceId,
        serviceDate,
        estimatedDurationSeconds: durationMinutes * 60,
        windowStartOffsetSeconds: parseOffsetTime(windowStart),
        windowEndOffsetSeconds: parseOffsetTime(windowEnd),
        timingMode,
        earliestServiceDate: timingMode === 'range' ? earliestServiceDate || serviceDate : null,
        deadlineServiceDate: timingMode === 'range' ? deadlineServiceDate || serviceDate : null,
        deadlineOffsetSeconds: deadlineOffsetSeconds ?? null,
        availabilityWindows: timingMode === 'multi_slot' ? (availabilityWindows ?? null) : null,
        priority,
        contactId: contactId ?? null,
        requiredSkillIds: selectedSkillIds,
      });
    }
  };

  const handleDelete = () => {
    if (!numJobId) return;
    if (confirm('Are you sure you want to delete this job?')) {
      deleteJobMutation.mutate({ id: numJobId });
    }
  };

  const isSaving = createJobMutation.isPending || updateJobMutation.isPending;

  if (mode === 'edit' && isJobLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading job...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Sticky Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={mode === 'edit' && numJobId ? `${basePath}/${numJobId}` : basePath}>
              <ArrowLeft className="h-4 w-4" />
              <span>{mode === 'edit' ? 'Job Details' : 'Jobs'}</span>
            </Link>
          </Button>

          <span className="text-muted-foreground/50">/</span>

          <h1 className="text-lg font-bold tracking-tight text-foreground truncate max-w-md">
            {mode === 'create' ? 'Create New Job' : `Edit ${existingJob?.title || 'Job'}`}
          </h1>

          {mode === 'edit' && existingJob && (
            <Badge variant="outline" className="capitalize text-xs">
              {status.replace('_', ' ')}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2.5">
          {mode === 'edit' && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10 border-destructive/30 gap-1.5"
              onClick={handleDelete}
              disabled={deleteJobMutation.isPending || isSaving}
            >
              {deleteJobMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              <span>Delete</span>
            </Button>
          )}

          <Button variant="outline" size="sm" asChild disabled={isSaving}>
            <Link to={mode === 'edit' && numJobId ? `${basePath}/${numJobId}` : basePath}>
              Cancel
            </Link>
          </Button>

          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSaving || !title.trim() || !formattedAddress.trim()}
            className="gap-1.5"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{mode === 'create' ? 'Create Job' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      {/* Main Full Page Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-4xl w-full mx-auto space-y-6">
        {/* Section 1: Job Identity & Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              <span>Job Title & Priority</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Basic job naming, scope, and routing priority.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Job Title *</label>
              <Input
                placeholder="e.g. HVAC Compressor Overhaul or Plumbing Inspection"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Service Date *</label>
                <Input
                  type="date"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Priority Level</label>
                <Select value={String(priority)} onValueChange={(val) => setPriority(Number(val))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Normal Priority (0)</SelectItem>
                    <SelectItem value="5">High Priority (5)</SelectItem>
                    <SelectItem value="10">Urgent / SLA Sensitive (10)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section: Customer & Contact (CRM) */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                <span>Customer & Contact (CRM)</span>
              </CardTitle>
              {contactId && (
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="text-xs h-7 gap-1 text-muted-foreground hover:text-foreground"
                >
                  <Link
                    to={`/org/${orgId}/workspace/${workspaceId}/contacts/${contactId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>View Customer Profile</span>
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              )}
            </div>
            <CardDescription className="text-xs">
              Link this job to a customer record to track service history and contact details.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Customer Contact</label>
              <ContactPicker
                value={contactId}
                onChange={(val) => setContactId(val)}
                placeholder="Search customer name, email, or company..."
              />
            </div>

            {selectedContact && (
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border/50 text-xs">
                <span className="font-semibold text-foreground">
                  {selectedContact.displayName || selectedContact.label}
                </span>
                {selectedContact.companyName && (
                  <Badge variant="secondary" className="text-[10px]">
                    {selectedContact.companyName}
                  </Badge>
                )}
                {selectedContact.emails?.[0]?.email && (
                  <span className="text-muted-foreground">{selectedContact.emails[0].email}</span>
                )}
                {selectedContact.phones?.[0]?.phone && (
                  <span className="text-muted-foreground">{selectedContact.phones[0].phone}</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Address & Location (Google Places Autocomplete) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Job Location & Address</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Physical destination with Google Places Autocomplete to auto-resolve coordinates and
              place ID.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Search Address or Place *
              </label>
              <AddressAutocomplete
                value={formattedAddress}
                onChange={setFormattedAddress}
                onSelectAddress={handleSelectAddress}
                initialLatitude={latitude}
                initialLongitude={longitude}
                placeholder="Start typing an address or landmark (e.g. 100 Commercial Rd, Sydney)..."
                required
              />
            </div>

            {latitude != null && longitude != null && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground p-2.5 rounded-lg bg-muted/40 border border-border/40 font-mono">
                <span>Lat: {latitude.toFixed(5)}</span>
                <span>Lng: {longitude.toFixed(5)}</span>
                {googlePlaceId && <span className="truncate">Place ID: {googlePlaceId}</span>}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Confirm on Map</label>
              <p className="text-[11px] text-muted-foreground">
                Can't find the address? Drag the pin or click the map to place it manually — useful
                for new developments not yet in Google's map data.
              </p>
              <JobLocationMap
                latitude={latitude}
                longitude={longitude}
                onPinChange={(lat, lng) => {
                  setLatitude(lat);
                  setLongitude(lng);
                  setGooglePlaceId(null);
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Duration & Time Windows */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>Duration & Time Window</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Estimated duration on-site and customer access delivery window.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5 max-w-xs">
              <label className="text-xs font-medium text-foreground">On-Site Duration (mins)</label>
              <Input
                type="number"
                min={15}
                step={15}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                required
              />
              <span className="text-[11px] text-muted-foreground">
                Estimated time spent completing this job on-site.
              </span>
            </div>

            <JobTimingConstraintsInput
              value={{
                timingMode,
                serviceDate,
                windowStartOffsetSeconds: parseOffsetTime(windowStart),
                windowEndOffsetSeconds: parseOffsetTime(windowEnd),
                earliestServiceDate,
                deadlineServiceDate,
                deadlineOffsetSeconds,
                availabilityWindows,
              }}
              onChange={(val) => {
                setTimingMode(val.timingMode);
                setServiceDate(val.serviceDate);
                setWindowStart(
                  val.windowStartOffsetSeconds != null
                    ? formatOffsetTime(val.windowStartOffsetSeconds)
                    : '08:00'
                );
                setWindowEnd(
                  val.windowEndOffsetSeconds != null
                    ? formatOffsetTime(val.windowEndOffsetSeconds)
                    : '17:00'
                );
                setEarliestServiceDate(val.earliestServiceDate ?? null);
                setDeadlineServiceDate(val.deadlineServiceDate ?? null);
                setDeadlineOffsetSeconds(val.deadlineOffsetSeconds ?? null);
                setAvailabilityWindows(val.availabilityWindows ?? null);
              }}
            />
          </CardContent>
        </Card>

        {/* Section 4: Required Skills & Capabilities */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-primary" />
                  <span>Required Skills & Qualifications</span>
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  Only operators possessing all selected skills will be assigned to this job by the
                  solver.
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs font-medium">
                {selectedSkillIds.length} Required
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {isSkillsLoading ? (
              <div className="text-xs text-muted-foreground py-4 text-center">
                Loading skills...
              </div>
            ) : skills.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                {skills.map((skill) => {
                  const isSelected = selectedSkillIds.includes(skill.id);
                  return (
                    <div
                      key={skill.id}
                      onClick={() => {
                        setSelectedSkillIds((prev) =>
                          isSelected ? prev.filter((id) => id !== skill.id) : [...prev, skill.id]
                        );
                      }}
                      className={`cursor-pointer rounded-lg border p-3 flex items-start gap-2.5 transition-all select-none ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                          : 'border-border/60 bg-card hover:bg-muted/40'
                      }`}
                    >
                      <div
                        className={`h-4 w-4 rounded mt-0.5 flex items-center justify-center border transition-colors ${
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-muted-foreground/40 bg-background'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="flex flex-col flex-1 overflow-hidden">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs text-foreground truncate">
                            {skill.name}
                          </span>
                          {skill.color && (
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: skill.color }}
                            />
                          )}
                        </div>
                        {skill.description && (
                          <span className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                            {skill.description}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg">
                <AlertTriangle className="h-6 w-6 mx-auto mb-1 opacity-40 text-muted-foreground" />
                <span>No skills created in catalog yet. Add one below:</span>
              </div>
            )}

            {/* Inline Quick Add Skill Box */}
            <div className="p-4 rounded-lg border border-border/60 bg-muted/20 space-y-3">
              <div className="flex items-center gap-2">
                <Plus className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground">
                  Add New Required Skill
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  placeholder="Skill name (e.g. Height Safety, Gas Fitting)..."
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  className="text-xs h-8"
                />
                <Input
                  placeholder="Optional description..."
                  value={newSkillDescription}
                  onChange={(e) => setNewSkillDescription(e.target.value)}
                  className="text-xs h-8"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-7 text-xs gap-1.5"
                  disabled={!newSkillName.trim() || createSkillMutation.isPending}
                  onClick={handleQuickAddSkill}
                >
                  {createSkillMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                  <span>Create & Require Skill</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Proof of Work & Photo Attachments (Edit Mode) */}
        {mode === 'edit' && numJobId > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="h-4 w-4 text-primary" />
                <span>Job Photos & Proof of Work</span>
              </CardTitle>
              <CardDescription className="text-xs">
                Upload and organize completion photos, site documentation, and signed receipts.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <JobAttachmentsSection jobId={numJobId} />
            </CardContent>
          </Card>
        )}

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 pt-4 pb-12 border-t border-border/40">
          <Button variant="outline" asChild disabled={isSaving}>
            <Link to={mode === 'edit' && numJobId ? `${basePath}/${numJobId}` : basePath}>
              Cancel
            </Link>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !title.trim() || !formattedAddress.trim()}
            className="gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{mode === 'create' ? 'Create Job' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
